/**
 * sim/clock.ts — Simulation clock with time-warp support
 *
 * Runs at 1× real time by default. Autopilot may drive time-warp.
 * JD computation for ephemeris lookups. Warp factor is diegetic:
 * console shows "×1 → ×50,000 → ×1" during autopilot.
 */

// Simulation epoch: J2000.0 = JD 2451545.0 = 2000-01-01 12:00:00 TT
export const J2000 = 2451545.0;
const MS_PER_DAY = 86400000;

// Clock state
let _warpFactor = 1.0;
let _simElapsedDays = 0.0;   // simulated days elapsed since boot
let _realBootTime = Date.now();
let _lastTickReal = _realBootTime;
let _paused = false;

/** Get current Julian Date (simulated) */
export function getCurrentJD(): number {
  const now = Date.now();
  const realDeltaMs = now - _realBootTime;
  const simDeltaMs = realDeltaMs * _warpFactor;
  const simDeltaDays = simDeltaMs / MS_PER_DAY;
  const bootJD = dateToJD(new Date(_realBootTime));
  return bootJD + simDeltaDays;
}

/** Advance the clock by one frame's dt (real ms) */
export function tickClock(realDtMs: number): number {
  if (_paused) return _warpFactor;

  const simDtMs = realDtMs * _warpFactor;
  _simElapsedDays += simDtMs / MS_PER_DAY;
  _lastTickReal = Date.now();
  return _warpFactor;
}

/** Get current time-warp factor */
export function getWarpFactor(): number {
  return _warpFactor;
}

/** Set time-warp factor (for autopilot) */
export function setWarpFactor(f: number): void {
  _warpFactor = Math.max(1.0, f);
}

/** Pause/resume */
export function setPaused(p: boolean): void {
  _paused = p;
}

export function isPaused(): boolean {
  return _paused;
}

/** Reset boot time (on session restore) */
export function resetBootTime(): void {
  _realBootTime = Date.now();
  _lastTickReal = _realBootTime;
  _simElapsedDays = 0;
}

/** Get simulated elapsed days */
export function getSimElapsedDays(): number {
  return _simElapsedDays;
}

/** Convert a JS Date to JD */
export function dateToJD(d: Date): number {
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate() + d.getUTCHours() / 24 + d.getUTCMinutes() / 1440 + d.getUTCSeconds() / 86400;

  let y = year;
  let m = month;
  if (m <= 2) {
    y -= 1;
    m += 12;
  }
  const A = Math.floor(y / 100);
  const B = 2 - A + Math.floor(A / 4);
  const jd = Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + day + B - 1524.5;
  return jd;
}

/** Seconds per day */
export const SECONDS_PER_DAY = 86400;
